import { ClassificationGroup } from "types/Models/classification";
import * as Yup from "yup";

const classNamesValidation = (classificationGroups: ClassificationGroup[]) =>
  Yup.array()
    .of(Yup.string().required("Class name is required"))
    .min(1, "At least one class name is required")
    .test(
      "unique-class-names",
      "Class names must be unique within the group",
      function (value) {
        if (!value || !Array.isArray(value)) return true;
        const lowerCaseNames = value
          .map((name) => name?.trim().toLowerCase())
          .filter(Boolean);
        return lowerCaseNames.length === new Set(lowerCaseNames).size;
      }
    )
    .test(
      "unique-name-class-combination",
      "Some class names already exist in the specified group",
      function (value) {
        const { name } = this.parent;
        if (!value || !name || !Array.isArray(value)) return true;

        return !value.some((className) =>
          classificationGroups.some(
            (group) =>
              group.name.trim().toLowerCase() === name.trim().toLowerCase() &&
              group.classes?.some(
                (cls) =>
                  cls.name.trim().toLowerCase() ===
                  className?.trim().toLowerCase()
              )
          )
        );
      }
    );

export const addNewGroup = (classificationGroups: ClassificationGroup[]) =>
  Yup.object({
    name: Yup.string()
      .required("Group name is required")
      .test("unique-group-name", "Group name already exists", function (value) {
        if (!value) return true;
        return !classificationGroups.some(
          (group) =>
            group.name.trim().toLowerCase() === value.trim().toLowerCase()
        );
      }),
    class_names: classNamesValidation(classificationGroups),
  });

export const addNewClass = (classificationGroups: ClassificationGroup[]) =>
  Yup.object({
    name: Yup.string().required("Group name is required"),
    class_names: classNamesValidation(classificationGroups),
  });

export const editGroup = (
  classificationGroups: ClassificationGroup[],
  currentName: string
) =>
  Yup.object({
    name: Yup.string()
      .required("Group name is required")
      .test("unique-group-name", "Group name already exists", function (value) {
        if (!value) return true;
        if (value.trim().toLowerCase() === currentName.trim().toLowerCase())
          return true;
        return !classificationGroups.some(
          (group) =>
            group.name.trim().toLowerCase() === value.trim().toLowerCase()
        );
      }),
  });

export const editClass = (
  classificationGroups: ClassificationGroup[],
  groupName: string,
  currentName: string
) =>
  Yup.object({
    groupName: Yup.string().required("Group name is required"),
    className: Yup.string()
      .required("Class name is required")
      .test(
        "unique-class-name",
        "Class name already exists in this group",
        function (value) {
          if (!value) return true;
          if (value.trim().toLowerCase() === currentName.trim().toLowerCase())
            return true;

          const group = classificationGroups.find(
            (g) =>
              g.name.trim().toLowerCase() === groupName.trim().toLowerCase()
          );
          if (!group) return true;

          return !group.classes.some(
            (cls) =>
              cls.name.trim().toLowerCase() === value.trim().toLowerCase()
          );
        }
      ),
  });
